import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";

export default function StaffInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({ name: "", password: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/staff/invite/${token}`).then((r) => { setData(r.data); setForm((f) => ({ ...f, name: r.data.invite.name || "" })); }).catch((e) => setErr(apiErr(e)));
  }, [token]);

  const accept = async () => {
    if (form.password.length < 8) return toast.error("Password must be at least 8 characters");
    setBusy(true);
    try {
      await api.post(`/staff/invite/${token}/accept`, form);
      toast.success("Welcome to the team!");
      if (refresh) await refresh();
      navigate("/app");
    } catch (e) { toast.error(apiErr(e)); setBusy(false); }
  };

  if (err) return <div className="min-h-screen flex items-center justify-center bg-background grain px-4"><div className="text-center"><h1 className="font-heading font-bold text-2xl">Invitation unavailable</h1><p className="text-muted-foreground mt-2">{err}</p><Link to="/login"><Button className="mt-6" variant="outline">Go to login</Button></Link></div></div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center bg-background"><div className="h-10 w-10 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>;

  const { invite, club } = data;
  const color = club?.primary_color || "#007AFF";

  return (
    <div className="min-h-screen bg-background grain flex items-center justify-center px-4">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 shadow-xl">
        <div className="flex items-center gap-3 mb-5">
          {club?.logo ? <img src={club.logo} alt="" className="h-12 w-12 rounded-xl object-cover" /> : <div className="h-12 w-12 rounded-xl flex items-center justify-center text-white" style={{ background: color }}><ShieldCheck size={22} /></div>}
          <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Staff invitation</p><h1 className="font-heading font-bold text-xl" data-testid="staff-invite-club">{club?.name}</h1></div>
        </div>
        <p className="text-sm text-muted-foreground mb-5">You're invited to join <b className="text-foreground">{club?.name}</b> as <b className="text-foreground">{invite.club_role}</b>. Set your password to activate your account (<span className="font-mono text-xs">{invite.email}</span>).</p>
        <div className="space-y-3">
          <div><Label>Your name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 h-11" data-testid="staff-accept-name" /></div>
          <div><Label>Create a password</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="At least 8 characters" className="mt-1.5 h-11" data-testid="staff-accept-password" /></div>
        </div>
        <Button onClick={accept} disabled={busy} className="w-full mt-5 h-11 text-white border-0" style={{ background: color }} data-testid="staff-accept-btn">{busy ? "Activating..." : "Accept & join"}</Button>
      </div>
    </div>
  );
}
